import { useEffect, useState, useContext } from "react";
import Toolbar from "../../../Shared/Toolbar/Toolbar";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { getPortalEntityTargetPage, updateItem } from "../../../../API/Portal/Portal";
import { MENU_ITEM_PROPERTIES } from "./utils";
import { NO_ENTITY_AVAILABLE_OPTION, NO_PAGE_AVAILABLE_OPTION } from "../CreateMenuItemDialog/utils";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../../Renderers/GlobalContext";
import { getEntities } from "../../../../API/Entity/Entity";

function RecentListForm({ itemId, portalId, projectId, properties, parentMenuItemId, onDelete, updateMenuItem }) {

    const [state, setState] = useState({
        ...properties,
        entities: [],
        pages: [],
        entity: {},
        page: {}
    })
    const [isChanged, setIsChanged] = useState(false);
    const { organization } = useContext(GlobalContext).state
    const { t, i18n } = useTranslation([
        "portal/menu_items"
    ], {
        useSuspense: false
    })


    useEffect(() => {
        async function getData() {
            // i18n.changeLanguage(organization.platformLanguage)
            const entities = await getEntities(projectId);
            const { data: pages } = await getPortalEntityTargetPage(portalId, properties.entityId);
            const entity = entities.find(entity => entity._id === properties.entityId) || NO_ENTITY_AVAILABLE_OPTION;
            const page = pages.find(page => page._id === properties.targetPageId) || NO_PAGE_AVAILABLE_OPTION;
            setState(prevState => ({
                ...prevState,
                ...properties,
                entities,
                pages,
                entity,
                page
            }))
        }
        getData();
    }, [itemId])

    useEffect(() => {
        handleFormChange()
    }, [state])

    useEffect(() => {
        async function getPages() {
            const { data: pages } = await getPortalEntityTargetPage(portalId, state.entity._id);
            const stateToUpdate = {
                pages,
                page: {}
            }
            const page = pages.find(page => page._id === properties.targetPageId) || NO_PAGE_AVAILABLE_OPTION;
            if (page) {
                stateToUpdate.page = page;
            }
            setState(prevState => ({
                ...prevState,
                ...stateToUpdate
            }))
        }

        if (state.entity && state.entity._id) {
            getPages();
        }
    }, [state.entity])

    function handleChange(event) {
        const { name, value } = event.target;
        setState(prevState => ({
            ...prevState,
            [name]: value
        }))
    }

    function handleReset() {
        const entity = state.entities.find(entity => entity._id === properties.entityId);
        const page = state.pages.find(page => page._id === properties.targetPageId);
        setState(prevState => ({
            ...prevState,
            ...properties,
            entity,
            page
        }))
    }

    function handleFormChange() {
        let isChanged = false;
        for (const property of MENU_ITEM_PROPERTIES) {
            switch (property) {
                case "entityId":
                    if (state.entity._id !== properties.entityId) {
                        isChanged = true;
                    }
                    break;
                case "targetPageId":
                    if (state.page._id !== properties.targetPageId) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (state[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
        }
        setIsChanged(isChanged);
    }

    async function handleSave() {
        const properties = {};
        for (const property of MENU_ITEM_PROPERTIES) {
            switch (property) {
                case "entityId":
                    properties[property] = state.entity._id;
                    break;
                case "targetPageId":
                    properties[property] = state.page._id;
                    break;
                default:
                    properties[property] = state[property];
                    break;
            }
        }
        await updateItem(portalId, parentMenuItemId, properties, itemId);
        updateMenuItem(parentMenuItemId, itemId, properties);
    }

    return (
        <div>
            <Toolbar
                name={state.name}
                type={state.type}
                disabled={!isChanged}
                iconclass="fas fa-send-backward"
                onNameChange={handleChange}
                onReset={handleReset}
                onSave={handleSave}
                onDelete={onDelete} />
            <div className="row m-0">
                <div className="p-2 col-md-6 col-lg-4">
                    <div className="form-label">
                        {t("edit_form.menu_item_name_field_label")}
                        {state.entity._id === "" && (
                            <i className="fas fa-exclamation-triangle error mx-1"></i>
                        )}
                    </div>
                    <DropDownList
                        name="entity"
                        onChange={handleChange}
                        className="form-control-custom"
                        value={state.entity}
                        data={state.entities}
                        textField="name"
                    />

                    <div className="form-label">
                        {t("edit_form.label_field_label")}
                    </div>
                    <input
                        placeholder="Enter Label"
                        className="form-control"
                        name="label"
                        onChange={handleChange}
                        value={state.label}
                        disabled={
                            state.showLabel === false ? true : false
                        }
                        maxLength={56}
                    />
                    <div className="form-label">
                        {t("edit_form.tooltip_field_label")}
                    </div>
                    <input
                        maxLength={56}
                        placeholder="Enter Tooltip"
                        className="form-control"
                        name="tooltip"
                        onChange={handleChange}
                        value={state.tooltip}
                    />

                    <div className="form-label">
                        {t("edit_form.number_of_lists_field_label")}
                    </div>
                    <DropDownList
                        name="recordsToShow"
                        onChange={handleChange}
                        className="form-control-custom"
                        value={state.recordsToShow}
                        data={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                    />

                    <div className="form-label">
                        {t("edit_form.target_page_field_label")}
                        {state.page._id === "" && (
                            <i className="fas fa-exclamation-triangle error mx-1"></i>
                        )}
                    </div>

                    <DropDownList
                        name="page"
                        onChange={handleChange}
                        className="form-control-custom"
                        value={state.page}
                        data={state.pages}
                        disabled={
                            state.entity._id === ""
                                ? true
                                : false
                        }
                        textField="pageName"
                        loading={state.pagesLoading}
                    />

                    <div className="form-label">
                        {t("edit_form.show_as_field_label")}
                    </div>
                    <DropDownList
                        name="showAs"
                        onChange={handleChange}
                        disabled={
                            state.page.subPageType !==
                            "New Record"
                        }
                        className="form-control-custom"
                        value={state.showAs}
                        data={["Inline", "Popup"]}
                    />
                </div>
            </div>
        </div>
    )
}

export default RecentListForm;